import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ShoppingCart, Webhook } from 'lucide-react'
import { useVerticalUI } from '@/lib/verticalUI'
import { getVerticalAppointmentWebhookCopy } from '@/services/orderWebhooksApi'

export interface OrdersNavTabItem {
  name: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
}

function isTabActive(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`)
}

export default function OrdersNavTabs({ className }: { className?: string }) {
  const location = useLocation()
  const { slug, profile } = useVerticalUI()
  const webhookCopy = getVerticalAppointmentWebhookCopy(slug)

  const tabs = useMemo<OrdersNavTabItem[]>(() => {
    const ordersLabel = profile.nav.showOrders
      ? profile.nav.primaryNavLabel
      : 'Historial de órdenes'
    const webhooksDescription = webhookCopy.isAppointmentVertical
      ? 'Agenda, Google Sheets, Calendar y automatizaciones'
      : 'ERP, Sheets y automatizaciones'

    return [
      {
        name: ordersLabel,
        path: '/app/orders',
        icon: ShoppingCart,
        description: 'Seguimiento y cierre comercial',
      },
      {
        name: webhookCopy.pageTitle,
        path: '/app/order-webhooks',
        icon: Webhook,
        description: webhooksDescription,
      },
    ]
  }, [profile, webhookCopy])

  return (
    <div
      data-testid="orders-nav-tabs"
      className={`border-b border-slate-200 bg-white/90 backdrop-blur px-3 py-2 sm:px-4 sm:rounded-2xl sm:border sm:shadow-sm mb-5 ${className || ''}`}
    >
      <nav
        className="flex space-x-1.5 sm:space-x-2 overflow-x-auto py-0.5 scrollbar-none"
        aria-label="Submenú de Órdenes"
      >
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = isTabActive(location.pathname, tab.path)

          return (
            <Link
              key={tab.path}
              to={tab.path}
              className={[
                'flex items-center gap-2 whitespace-nowrap rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all',
                isActive
                  ? 'bg-[#075f55] text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              ].join(' ')}
              title={tab.description}
            >
              <Icon className={`h-4 w-4 ${isActive ? 'text-white' : 'text-slate-500'}`} />
              <span>{tab.name}</span>
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
